/**
 * Business Tag Generator
 * Generates SEO tags from description, category, and services
 * Note: Business name is NOT used in tag generation
 */

const STOP_WORDS = new Set([
  "the", "and", "for", "with", "from", "shop", "store", "showroom", "best", "good",
  "in", "on", "of", "at", "a", "an", "to", "by", "near", "world", "center",
  "services", "service", "dealer", "dealers", "our", "your", "we", "you", "all",
  "are", "is", "was", "been", "being", "have", "has", "had", "do", "does", "did",
  "will", "would", "could", "should", "may", "might", "must", "shall", "can",
  "this", "that", "these", "those", "it", "its", "they", "them", "their",
  "here", "there", "where", "when", "what", "which", "who", "whom", "how", "why",
  "also", "just", "only", "very", "more", "most", "other", "some", "any", "each",
  "every", "both", "few", "many", "much", "own", "same", "than", "too", "such",
  "get", "got", "getting", "make", "made", "making", "take", "taking", "come",
  "offer", "offering", "offers", "offered", "provide", "providing", "provides",
  "one", "two", "three", "four", "five", "first", "second", "third", "new", "old",
  "high", "low", "big", "small", "great", "well", "better", "top", "quality",
  "based", "located", "available", "specializing", "dedicated", "committed",
  "leading", "trusted", "reliable", "professional", "experienced", "expert"
]);

const BUSINESS_TYPE_SUFFIXES = ["Store", "Shop", "Showroom", "Center", "Hub", "Point", "Mart", "House"];

const MAX_TAGS = 40;
const MAX_DESCRIPTION_KEYWORDS = 5;

function slugify(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toTitleCase(str) {
  if (!str) return '';
  return str
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function extractKeywords(str) {
  if (!str) return [];
  const tokens = str
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(token => token.length >= 3)
    .filter(token => !STOP_WORDS.has(token));
  return [...new Set(tokens)];
}

function detectBusinessTypes(description, categories) {
  const types = [];
  const text = (description || '').toLowerCase();
  const cats = (categories || []).map(c => (c || '').toLowerCase());
  
  const typePatterns = [
    { pattern: /electronics?/i, type: "Electronics" },
    { pattern: /appliance/i, type: "Appliance" },
    { pattern: /medical|clinic|hospital|healthcare/i, type: "Healthcare" },
    { pattern: /restaurant|food|cafe|eatery/i, type: "Restaurant" },
    { pattern: /salon|spa|beauty|parlour|parlor/i, type: "Beauty" },
    { pattern: /gym|fitness|workout/i, type: "Fitness" },
    { pattern: /plumb/i, type: "Plumbing" },
    { pattern: /electric/i, type: "Electrical" },
    { pattern: /auto|car|vehicle|garage/i, type: "Automotive" },
    { pattern: /cloth|fashion|apparel|garment/i, type: "Fashion" },
    { pattern: /jewel/i, type: "Jewellery" },
    { pattern: /furniture/i, type: "Furniture" },
    { pattern: /grocery|supermarket/i, type: "Grocery" },
    { pattern: /pharmacy|medical store|chemist/i, type: "Pharmacy" },
    { pattern: /hotel|resort|lodge/i, type: "Hotel" },
    { pattern: /education|school|coaching|tuition/i, type: "Education" },
    { pattern: /lawyer|advocate|legal/i, type: "Legal" },
    { pattern: /real estate|property/i, type: "Real Estate" },
    { pattern: /travel|tour/i, type: "Travel" },
    { pattern: /insurance/i, type: "Insurance" },
    { pattern: /bank|finance|loan/i, type: "Finance" },
    { pattern: /computer|laptop|it /i, type: "Computer" },
    { pattern: /mobile|phone|smartphone/i, type: "Mobile" },
    { pattern: /ac |air condition/i, type: "AC" },
    { pattern: /chimney/i, type: "Chimney" },
    { pattern: /tv|television|led/i, type: "TV" }
  ];

  for (const { pattern, type } of typePatterns) {
    if (pattern.test(text) || cats.some(c => pattern.test(c))) {
      types.push(type);
    }
  }
  
  return [...new Set(types)];
}

function generateVariations(baseText, type) {
  const variations = [];
  const suffixes = ["Dealer", "Store", "Shop", "Showroom", "Service", "Repair"];
  
  for (const suffix of suffixes) {
    if (!baseText.toLowerCase().includes(suffix.toLowerCase())) {
      variations.push(`${baseText} ${suffix}`);
    }
  }
  
  return variations.slice(0, 3);
}

function makeTag(text, type, priority, seenTags) {
  if (!text) return null;
  const trimmed = text.trim();
  if (trimmed.length < 3 || trimmed.length > 60) return null;
  
  const titleCased = toTitleCase(trimmed);
  const key = titleCased.toLowerCase();
  
  if (seenTags.has(key)) return null;
  seenTags.add(key);
  
  return {
    text: titleCased,
    url: "/search?q=" + encodeURIComponent(titleCased),
    slug: slugify(trimmed),
    type,
    priority
  };
}

export function generateBusinessTags(input) {
  if (!input) return { totalTags: 0, tags: [] };

  const {
    description = '',
    services = [],
    products = [],
    categories = [],
    locality = '',
    city = '',
    state = ''
  } = input;

  const rawTags = [];
  const seenTags = new Set();

  const descKeywords = extractKeywords(description).slice(0, MAX_DESCRIPTION_KEYWORDS);
  const serviceLikeList = [...(services || []), ...(products || [])].filter(Boolean);
  const categoryList = (categories || []).filter(Boolean);
  const detectedTypes = detectBusinessTypes(description, categories);
  
  const hasLocation = !!(locality || city);

  // a. Services/Products as primary tags (highest priority)
  for (const s of serviceLikeList) {
    const tag = makeTag(s, 'service', 10, seenTags);
    if (tag) rawTags.push(tag);
    
    const bestTag = makeTag(`Best ${s}`, 'service', 9, seenTags);
    if (bestTag) rawTags.push(bestTag);
  }

  // b. Categories with variations
  for (const cat of categoryList) {
    const tag = makeTag(cat, 'business_type', 10, seenTags);
    if (tag) rawTags.push(tag);
    
    const bestTag = makeTag(`Best ${cat}`, 'business_type', 9, seenTags);
    if (bestTag) rawTags.push(bestTag);
    
    const variations = generateVariations(cat, 'business_type');
    for (const v of variations) {
      const vTag = makeTag(v, 'business_type', 8, seenTags);
      if (vTag) rawTags.push(vTag);
    }
  }

  // c. Detected business types with suffixes
  for (const bizType of detectedTypes) {
    for (const suffix of BUSINESS_TYPE_SUFFIXES.slice(0, 3)) {
      const tag = makeTag(`${bizType} ${suffix}`, 'business_type', 8, seenTags);
      if (tag) rawTags.push(tag);
    }
  }

  // d. Main description keywords (specific, not generic)
  for (const k of descKeywords) {
    if (k.length >= 4) {
      const tag = makeTag(k, 'general', 7, seenTags);
      if (tag) rawTags.push(tag);
    }
  }

  // e. Location-based tags (only if location provided)
  if (hasLocation) {
    const locations = [
      { name: locality, basePriority: 9 },
      { name: city, basePriority: 8 },
      { name: state, basePriority: 7 }
    ].filter(loc => loc.name);

    for (const loc of locations) {
      for (const s of serviceLikeList.slice(0, 5)) {
        const tag = makeTag(`${s} In ${loc.name}`, 'location', loc.basePriority, seenTags);
        if (tag) rawTags.push(tag);
      }
      for (const cat of categoryList.slice(0, 3)) {
        const tag = makeTag(`${cat} In ${loc.name}`, 'location', loc.basePriority, seenTags);
        if (tag) rawTags.push(tag);
      }
      for (const bizType of detectedTypes.slice(0, 2)) {
        const tag = makeTag(`${bizType} In ${loc.name}`, 'location', loc.basePriority, seenTags);
        if (tag) rawTags.push(tag);
      }
    }
  }

  // f. "Near Me" proximity tags
  for (const s of serviceLikeList.slice(0, 5)) {
    const tag = makeTag(`${s} Near Me`, 'proximity', 8, seenTags);
    if (tag) rawTags.push(tag);
  }
  for (const cat of categoryList.slice(0, 3)) {
    const tag = makeTag(`${cat} Near Me`, 'proximity', 8, seenTags);
    if (tag) rawTags.push(tag);
  }

  // Sort by priority descending
  rawTags.sort((a, b) => b.priority - a.priority);

  // Limit to MAX_TAGS
  const limited = rawTags.slice(0, MAX_TAGS);

  // Assign IDs
  const tags = limited.map((tag, index) => ({
    id: index + 1,
    ...tag
  }));

  return {
    totalTags: tags.length,
    tags
  };
}

export default generateBusinessTags;
